import { createAsyncThunk } from "@reduxjs/toolkit";
import { parseError } from "../utils/shared";

/**
 * Creates a wrapped async thunk with built-in error handling
 * 
 * @param {string} typePrefix - The action type prefix for the thunk (e.g., 'user/fetchUser')
 * @param {Function} asyncFunction - The async function to execute. Receives (payload, thunkAPI) as arguments
 * @param {boolean} [showError=true] - Whether to show error notifications/messages when the thunk fails
 * 
 * @returns {Function} A Redux async thunk with automatic error handling and parsing
 * 
 * @property {Function} pending - Action creator for pending state (typePrefix/pending)
 * @property {Function} fulfilled - Action creator for fulfilled state (typePrefix/fulfilled)
 * @property {Function} rejected - Action creator for rejected state (typePrefix/rejected)
 *
 */
export const createWrappedAsyncThunk = (
  typePrefix,
  asyncFunction,
  showError = true
) => {
  return createAsyncThunk(typePrefix, async (payload, thunkAPI) => {
    try {
      const result = await asyncFunction(payload, thunkAPI);
      return result;
    } catch (error) {
      const parsedError = parseError(error, showError);
      return thunkAPI.rejectWithValue(parsedError);
    }
  });
};
